import { StoreBase } from '@tk/basics/tk.store.base';

type DeviceID = string;

export enum ProductSelectActions {
    SET_BASE_DEVICE = 'setBaseDevice',
    SET_COUNTERPART_DEVICES = 'setCounterpartDevices',
    SET_REMOTES_BASE = 'setRemotesBase',
    SET_REMOTES_COUNTERPART = 'setRemotesCounterpart',
    SET_ACTIVE_REMOTE = 'setActiveRemote',
    SET_BASE_QUANTITY = 'setBaseQuantity',
    SET_COUNTERPART_QUANTITY = 'setCounterpartQuantity',
    SET_COUNTERPART_ACTIVE = 'setCounterpartActive',
    SET_COUNTERPART_INACTIVE = 'setCounterpartInactive',
    SET_TOTAL_PRICE = 'setTotalPrice',
    RESET_STORE = 'resetStore',
    SET_ACTIVE_GRID = 'setActiveGrid',
    SET_GRIDS_BASE = 'setGridsBase',
    SET_GRIDS_COUNTERPART = 'setGridsCounterpart',
    SET_PRICES = 'setPrices',
    SET_ACCESSORY_PRICES = 'setAccessoryPrices',
}

export enum DeviceType {
    INDOOR = 'I',
    OUTDOOR = 'O',
    REMOTE = 'R',
    GRID = 'G',
}

export enum OutdoorDeviceType {
    SINGLE,
    MULTI,
}

export enum ProductSelectCounterTypes {
    BASE = 'base',
    COUNTERPART = 'counterpart',
}

export enum PriceScope {
    BASE = ProductSelectCounterTypes.BASE,
    COUNTERPART = ProductSelectCounterTypes.COUNTERPART,
}

export enum AccessoryType {
    REMOTE = 'remote',
    GRID = 'grid',
}

interface Device {
    id: DeviceID;
    artNo: string;
    title: string;
    subtitle?: string;
    price: string;
    uvpPrice: string;
    image?: string;
    deviceType?: DeviceType;
}

interface OutdoorDevice extends Device {
    outdoorDeviceType?: OutdoorDeviceType;
}

export interface RemoteDevice extends Device {
    parentId: string;
    inclusive: boolean;
    isActive: boolean;
    hasSiblings: boolean;
}

export interface Grid extends Device {
    parentId: string;
    inclusive: boolean;
    isActive: boolean;
    hasSiblings: boolean;
}

export interface IndoorDevice extends Device {
    powerOutput: string;
    remoteDevices: RemoteDevice[];
    remotesInitalized: boolean;
    isCassette: boolean;
    gridCollection: Grid[];
    gridsInitalized: boolean;
}

export interface CounterpartDevice extends OutdoorDevice, IndoorDevice {
    quantity?: number;
    isActive: boolean;
}

export interface BaseDevice extends OutdoorDevice, IndoorDevice {
    quantity: number;
}

interface CounterpartDevices {
    quantity: number;
    devices: CounterpartDevice[];
}

interface ProductSelectStore {
    totalPrice: number;
    totalUvp: number;
    baseDevice?: BaseDevice;
    counterpartDevices: CounterpartDevices;
    isLoggedIn: boolean;
}

export interface PriceResponse {
    articles: {
        id: string;
        price: string;
    }[];
}
export interface StateResponse {
    item: DeviceID;
    gridHasSiblings: boolean;
    remoteHasSiblings: boolean;
}

export default class TCProductSelectStore extends StoreBase<ProductSelectStore> {
    constructor() {
        super({
            totalPrice: 0,
            baseDevice: {
                id: '',
                artNo: '',
                title: '',
                subtitle: '',
                powerOutput: '',
                price: '',
                uvpPrice: '',
                image: '',
                deviceType: undefined,
                quantity: 0,
                outdoorDeviceType: undefined,
                remoteDevices: [],
                remotesInitalized: false,
                isCassette: false,
                gridCollection: [],
                gridsInitalized: false,
            },
            counterpartDevices: {
                quantity: 0,
                devices: [],
            },
            totalUvp: 0,
            isLoggedIn: false,
        });
    }

    async initialize(): Promise<void> {}

    // ======= Setter =======
    setBaseDevice(baseDevice: BaseDevice) {
        this.state.baseDevice = baseDevice;
        this.notify(ProductSelectActions.SET_BASE_DEVICE, this.state);
    }

    setCounterpartDevices(counterpartDevices: CounterpartDevice[]) {
        this.state.counterpartDevices.devices = counterpartDevices;
        this.state.counterpartDevices.quantity = counterpartDevices.length;
        this.notify(ProductSelectActions.SET_COUNTERPART_DEVICES, this.state);
    }

    setRemoteDevices(deviceId: DeviceID, remoteDevices: RemoteDevice[]) {
        if (this.state.baseDevice?.id === deviceId) {
            this.state.baseDevice.remoteDevices = remoteDevices;
            this.state.baseDevice.remotesInitalized = true;
            this.notify(ProductSelectActions.SET_REMOTES_BASE, this.state);
            return;
        }

        const counterpartDevice = this.state.counterpartDevices.devices.find((device) => device.id === deviceId);
        if (!counterpartDevice) return;

        counterpartDevice.remoteDevices = remoteDevices;
        counterpartDevice.remotesInitalized = true;
        this.notify(ProductSelectActions.SET_REMOTES_COUNTERPART, this.state);
    }

    setGrids(deviceId: DeviceID, grids: RemoteDevice[]) {
        if (this.state.baseDevice?.id === deviceId) {
            this.state.baseDevice.gridCollection = grids;
            this.state.baseDevice.gridsInitalized = true;
            this.notify(ProductSelectActions.SET_GRIDS_BASE, this.state);
            return;
        }

        const counterpartDevice = this.state.counterpartDevices.devices.find((device) => device.id === deviceId);
        if (!counterpartDevice) return;

        counterpartDevice.gridCollection = grids;
        counterpartDevice.gridsInitalized = true;
        this.notify(ProductSelectActions.SET_GRIDS_COUNTERPART, this.state);
    }

    setActiveRemoteDevice(contextDeviceId: DeviceID, remoteId: DeviceID) {
        const device =
            this.state.baseDevice?.id === contextDeviceId
                ? this.state.baseDevice
                : this.state.counterpartDevices.devices.find((device) => device.id === contextDeviceId);
        if (!device) return;

        device.remoteDevices = device.remoteDevices.map((remote) => ({ ...remote, isActive: remote.id === remoteId }));
        this.notify(ProductSelectActions.SET_ACTIVE_REMOTE, this.state);
    }

    setActiveGrid(contextDeviceId: DeviceID, gridId: DeviceID) {
        const device =
            this.state.baseDevice?.id === contextDeviceId
                ? this.state.baseDevice
                : this.state.counterpartDevices.devices.find((device) => device.id === contextDeviceId);
        if (!device) return;

        device.gridCollection = device.gridCollection.map((grid) => ({ ...grid, isActive: grid.id === gridId }));
        this.notify(ProductSelectActions.SET_ACTIVE_GRID, this.state);
    }

    setDeviceQuantity(deviceID: DeviceID, quantity: number, counterType: ProductSelectCounterTypes) {
        if (counterType === ProductSelectCounterTypes.BASE) {
            if (this.state.baseDevice!.id !== deviceID) return;
            this.state.baseDevice!.quantity = quantity;
            this.notify(ProductSelectActions.SET_BASE_QUANTITY, this.state);
        } else if (counterType === ProductSelectCounterTypes.COUNTERPART) {
            const device = this.state.counterpartDevices.devices.find((device) => device.id === deviceID);
            if (!device) return;

            const statusChanged = this.setCounterpartDeviceActiveState(device, quantity);
            if (!statusChanged) this.notify(ProductSelectActions.SET_COUNTERPART_QUANTITY, this.state);
        }
    }

    recalcTotal(calcUvp: boolean) {
        this.recalcTotalPrice();
        calcUvp && this.recalcTotalUvp();
        this.notify(ProductSelectActions.SET_TOTAL_PRICE, this.state);
    }

    resetStore() {
        this.state.baseDevice && (this.state.baseDevice.quantity = 1);
        this.state.counterpartDevices.devices.forEach((device) => {
            device.quantity = 0;
            device.isActive = false;
        });

        this.recalcTotal(true);
        this.notify(ProductSelectActions.RESET_STORE, this.state);
    }

    setPrices(response: PriceResponse, scope: PriceScope): void {
        const articles = response.articles;

        if (scope === PriceScope.BASE) {
            this.applyPricesToDevice(this.state.baseDevice as IndoorDevice, articles);
        } else {
            for (const device of this.state.counterpartDevices.devices) {
                this.applyPricesToDevice(device as IndoorDevice, articles);
            }
        }

        this.notify(ProductSelectActions.SET_PRICES, this.state);
    }

    setAccessoryPrices(accessoryType: AccessoryType, contextDeviceId: DeviceID, response: PriceResponse) {
        const device = this.getDevice(contextDeviceId) as IndoorDevice;
        const accessories = accessoryType === AccessoryType.REMOTE ? device.remoteDevices : device.gridCollection;
        response.articles.forEach((article) => {
            const accessory = accessories.find((acc) => acc.id === article.id);
            if (accessory) accessory.price = article.price;
        });

        this.notify(ProductSelectActions.SET_ACCESSORY_PRICES, this.state);
    }

    setAccessoryState(response: StateResponse) {
        const device = this.getDevice(response.item) as IndoorDevice;
        const remote = device.remoteDevices[0];
        const grid = device.gridCollection[0];
        remote && (remote.hasSiblings = response.remoteHasSiblings);
        grid && (grid.hasSiblings = response.gridHasSiblings);
    }

    setLoginStatus(status: boolean): void {
        this.state.isLoggedIn = status;
    }

    // ======= Getter =======
    getDevice(id: DeviceID): Device | undefined {
        if (this.state.baseDevice?.id === id) return this.state.baseDevice;
        return this.state.counterpartDevices.devices.find((device) => device.id === id);
    }

    getRemoteDevices(contextDeviceId: DeviceID) {
        const device = this.getDevice(contextDeviceId) as IndoorDevice;
        if (device.deviceType !== DeviceType.INDOOR) return;
        return device.remoteDevices;
    }

    getRemoteIds(contextDeviceId: DeviceID) {
        const devices = this.getRemoteDevices(contextDeviceId);
        const ids: string[] = [];
        if (!devices || devices.length < 1) return ids.join(',');
        devices.filter((device) => device.inclusive === false).forEach((device) => ids.push(device.id.split('-')[1]));
        return ids.join(',');
    }

    getGrids(contextDeviceId: DeviceID) {
        const device = this.getDevice(contextDeviceId) as IndoorDevice;
        if (device.deviceType !== DeviceType.INDOOR) return;
        return device.gridCollection;
    }

    getGridIds(contextDeviceId: DeviceID) {
        const devices = this.getGrids(contextDeviceId);
        const ids: string[] = [];
        if (!devices || devices.length < 1) return ids.join(',');
        devices.filter((device) => device.inclusive === false).forEach((device) => ids.push(device.id.split('-')[1]));
        return ids.join(',');
    }

    getActiveRemote(id: DeviceID): RemoteDevice | undefined {
        const device = this.getDevice(id) as IndoorDevice;
        if (!device || device.deviceType !== DeviceType.INDOOR || device.remoteDevices.length === 0) return;
        return device.remoteDevices.find((remote) => remote.isActive === true) || device.remoteDevices[0];
    }

    getActiveGrid(id: DeviceID): Grid | undefined {
        const device = this.getDevice(id) as IndoorDevice;
        if (!device || device.deviceType !== DeviceType.INDOOR || device.gridCollection.length === 0) return;
        return device.gridCollection.find((grid) => grid.isActive === true) || device.gridCollection[0];
    }

    getDevicesToRender(): (CounterpartDevice | RemoteDevice)[] {
        const { baseDevice, counterpartDevices } = this.state;
        const isBaseOutdoor = baseDevice?.deviceType === DeviceType.OUTDOOR;
        const activeDevices = counterpartDevices.devices.filter((device) => device.isActive);
        if (activeDevices.length === 0 && counterpartDevices.devices.length > 0) return [counterpartDevices.devices[0]];

        return activeDevices.flatMap((device) => {
            const result = [];
            result.push(device);
            if (!isBaseOutdoor || device.deviceType !== DeviceType.INDOOR) return result;
            const activeRemote = this.getActiveRemote(device.id);
            activeRemote && result.push(activeRemote);
            const activeGrid = this.getActiveGrid(device.id);
            activeGrid && result.push(activeGrid);
            return result;
        });
    }

    getTotalPrice() {
        const { totalPrice } = this.state;
        return totalPrice;
    }

    getTotalUvp() {
        const { totalUvp } = this.state;
        return totalUvp;
    }

    gatherBasketData(): string {
        const parts: string[] = [];
        const { baseDevice, counterpartDevices } = this.state;
        if (baseDevice && baseDevice.quantity > 0) parts.push(...this.serializeDeviceWithRemote(baseDevice));
        counterpartDevices.devices.forEach((device) => {
            if (!device.isActive || !device.quantity || device.quantity < 1) return;
            parts.push(...this.serializeDeviceWithRemote(device));
        });
        return parts.join(',');
    }

    getBaseIds(): string {
        const ids: string[] = [];
        const baseDevice = this.state.baseDevice;
        if (!baseDevice) return '';

        ids.push(baseDevice.id);
        if (baseDevice.deviceType !== DeviceType.INDOOR) return ids.join(',');

        baseDevice.remoteDevices
            .filter((remote) => remote.inclusive === false)
            .forEach((remote) => ids.push(remote.id.split('-')[1]));

        baseDevice.gridCollection
            .filter((grid) => grid.inclusive === false)
            .forEach((grid) => ids.push(grid.id.split('-')[1]));

        return ids.join(',');
    }

    getCounterpartIds(): string {
        const ids: string[] = [];

        this.state.counterpartDevices.devices.forEach((device) => {
            ids.push(device.id);
            if (device.deviceType === DeviceType.INDOOR) return;

            device.remoteDevices
                .filter((remote) => remote.inclusive === false)
                .forEach((remote) => ids.push(remote.id.split('-')[1]));

            device.gridCollection
                .filter((grid) => grid.inclusive === false)
                .forEach((grid) => ids.push(grid.id.split('-')[1]));
        });

        return ids.join(',');
    }

    getCounterparts(): CounterpartDevice[] {
        return this.state.counterpartDevices.devices;
    }

    getLoginStatus(): boolean {
        return this.state.isLoggedIn;
    }

    // ======= Protected =======
    protected setCounterpartDeviceActiveState(device: CounterpartDevice, newQuantity: number): boolean {
        const wasActive = (device.quantity ?? 0) > 0;
        const willBeActive = newQuantity > 0;
        device.quantity = newQuantity;

        if (!wasActive && willBeActive) {
            device.isActive = true;
            this.notify(ProductSelectActions.SET_COUNTERPART_ACTIVE, this.state);
            return true;
        } else if (wasActive && !willBeActive) {
            device.isActive = false;
            this.notify(ProductSelectActions.SET_COUNTERPART_INACTIVE, this.state);
            return true;
        } else return false;
    }

    protected recalcTotalPrice() {
        let total = 0;
        const { baseDevice, counterpartDevices } = this.state;
        if (!baseDevice || !counterpartDevices) return;

        total += this.parsePrice(baseDevice.price) * baseDevice.quantity;
        if (baseDevice.deviceType === DeviceType.INDOOR) {
            total += this.addRemotePrice(baseDevice.remoteDevices, baseDevice.quantity);
            total += this.addGridPrice(baseDevice.gridCollection, baseDevice.quantity);
        }

        counterpartDevices.devices.forEach((device) => {
            if (!device.isActive || !device.quantity) return;

            total += this.parsePrice(device.price) * device.quantity;
            if (device.deviceType === DeviceType.INDOOR) {
                total += this.addRemotePrice(device.remoteDevices, device.quantity);
                total += this.addGridPrice(device.gridCollection, device.quantity);
            }
        });

        this.state.totalPrice = total;
    }

    protected recalcTotalUvp() {
        let total = 0;
        const { baseDevice, counterpartDevices } = this.state;
        if (!baseDevice || !counterpartDevices) return;

        total += this.parsePrice(baseDevice.uvpPrice) * baseDevice.quantity;
        if (baseDevice.deviceType === DeviceType.INDOOR) {
            total += this.addRemoteUvp(baseDevice.remoteDevices, baseDevice.quantity);
            baseDevice.isCassette && (total += this.addGridUvp(baseDevice.gridCollection, baseDevice.quantity));
        }

        counterpartDevices.devices.forEach((device) => {
            if (!device.isActive || !device.quantity) return;

            total += this.parsePrice(device.uvpPrice) * device.quantity;
            if (device.deviceType === DeviceType.INDOOR) {
                total += this.addRemoteUvp(device.remoteDevices, device.quantity);
                device.isCassette && (total += this.addGridUvp(device.gridCollection, device.quantity));
            }
        });

        this.state.totalUvp = total;
    }

    protected addGridPrice(grids: Grid[], quantity: number): number {
        const activeGrid = grids.find((grid) => grid.isActive);
        if (!activeGrid || activeGrid.inclusive) return 0;
        const numericPrice = parseFloat(activeGrid.price.replace(/'/g, ''));
        return isNaN(numericPrice) ? 0 : numericPrice * quantity;
    }

    protected addGridUvp(grids: Grid[], quantity: number): number {
        const activeGrid = grids.find((grid) => grid.isActive);
        if (!activeGrid || activeGrid.inclusive) return 0;
        const numericUvp = parseFloat(activeGrid.uvpPrice.replace(/'/g, ''));
        return isNaN(numericUvp) ? 0 : numericUvp * quantity;
    }

    protected addRemotePrice(remoteDevices: RemoteDevice[], quantity: number): number {
        const activeRemote = remoteDevices.find((remote) => remote.isActive);
        if (!activeRemote || activeRemote.inclusive) return 0;
        const numericPrice = parseFloat(activeRemote.price.replace(/'/g, ''));
        return isNaN(numericPrice) ? 0 : numericPrice * quantity;
    }

    protected addRemoteUvp(remoteDevices: RemoteDevice[], quantity: number): number {
        const activeRemote = remoteDevices.find((remote) => remote.isActive);
        if (!activeRemote || activeRemote.inclusive) return 0;
        const numericUvp = parseFloat(activeRemote.uvpPrice.replace(/'/g, ''));
        return isNaN(numericUvp) ? 0 : numericUvp * quantity;
    }

    protected parsePrice(price: string): number {
        const sanitized = price.replace(/'/g, '');
        const parsed = parseFloat(sanitized);
        return isNaN(parsed) ? 0 : parsed;
    }

    protected serializeDeviceWithRemote(device: Device & { quantity?: number }): string[] {
        const result: string[] = [];
        const quantity = device.quantity ?? 0;
        result.push(`${device.artNo}`, `${quantity}`);
        if (device.deviceType !== DeviceType.INDOOR) return result;

        const activeRemote = this.getActiveRemote(device.id);
        activeRemote && result.push(`${activeRemote.artNo}`, `${quantity}`);
        const activeGrid = this.getActiveGrid(device.id);
        activeGrid && result.push(`${activeGrid.artNo}`, `${quantity}`);
        return result;
    }

    protected applyPricesToDevice(device: IndoorDevice, articles: PriceResponse['articles']): void {
        const items = [device, ...(device.remoteDevices ?? []), ...(device.gridCollection ?? [])];

        for (const item of items) {
            const match = articles.find((a) => a.id === item.id);
            if (match) {
                item.price = match.price;
            }
        }
    }
}
